<template>
  <div class="position-detail-container">
    <el-page-header @back="goBack" :title="'职位详情'" class="page-header" />

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <div v-else-if="!position.id" class="not-found-container">
      <el-empty description="未找到职位信息" />
      <el-button @click="goBack" type="primary">返回职位列表</el-button>
    </div>

    <div v-else class="position-content">
      <el-card class="position-card">
        <template #header>
          <div class="card-header">
            <h2>{{ position.title }}</h2>
            <div class="header-actions">
              <el-button 
                type="primary" 
                @click="handleEdit"
                size="small"
                :icon="Edit"
              >
                编辑
              </el-button>
              <el-button 
                type="danger" 
                @click="confirmDelete"
                size="small"
                :icon="Delete"
              >
                删除
              </el-button>
            </div>
          </div>
        </template>

        <div class="position-info">
          <div class="info-row">
            <div class="info-item">
              <span class="label">公司</span>
              <span class="value">{{ position.company?.name || '未知' }}</span>
            </div>
            <div class="info-item">
              <span class="label">工作类型</span>
              <span class="value">{{ getWorkTypeLabel(position.workType) }}</span>
            </div>
          </div>

          <div class="info-row">
            <div class="info-item">
              <span class="label">薪资范围</span>
              <span class="value" v-if="position.minSalary && position.maxSalary">
                {{ position.minSalary }}-{{ position.maxSalary }} {{ position.salaryCurrency || 'CNY' }}
              </span>
              <span class="value" v-else>未设置</span>
            </div>
            <div class="info-item">
              <span class="label">优先级</span>
              <span class="value">
                <el-tag :type="getPriorityType(position.priority)" effect="plain">
                  {{ getPriorityLabel(position.priority) }}
                </el-tag>
              </span>
            </div>
          </div>

          <div class="info-row">
            <div class="info-item">
              <span class="label">申请状态</span>
              <span class="value">
                <application-status-tag :status="position.applicationStatus" />
              </span>
            </div>
            <div class="info-item">
              <span class="label">面试状态</span>
              <span class="value">
                <interview-status-tag :status="position.interviewStatus" />
              </span>
            </div>
          </div>

          <div class="info-row" v-if="position.deadline || position.nextInterviewDate">
            <div class="info-item" v-if="position.deadline">
              <span class="label">截止日期</span>
              <span class="value">{{ formatDate(position.deadline) }}</span>
            </div>
            <div class="info-item" v-if="position.nextInterviewDate">
              <span class="label">下次面试</span>
              <span class="value">{{ formatDate(position.nextInterviewDate) }}</span>
            </div>
          </div>
        </div>

        <div v-if="position.jobUrl" class="job-url-section">
          <h3>职位链接</h3>
          <el-link :href="position.jobUrl" target="_blank" type="primary">
            {{ position.jobUrl }}
            <el-icon class="el-icon--right"><Link /></el-icon>
          </el-link>
        </div>

        <div v-if="position.description" class="description-section">
          <h3>职位描述</h3>
          <div class="content-box">{{ position.description }}</div>
        </div>

        <div v-if="position.requirements" class="requirements-section">
          <h3>职位要求</h3>
          <div class="content-box">{{ position.requirements }}</div>
        </div>
      </el-card>
      
      <!-- 面试列表组件 -->
      <el-card class="interviews-card" id="interviews">
        <interview-list 
          :position-id="Number(positionId)" 
          @refresh="handleInterviewsRefresh"
        />
      </el-card>

      <!-- 资源列表 -->
      <ResourceList
        :position-id="position.id"
        @refresh="handleResourceRefresh"
      />
    </div>

    <el-dialog
      v-model="dialogVisible"
      title="编辑职位"
      width="60%"
      destroy-on-close
    >
      <position-form
        v-if="dialogVisible"
        :position="position"
        :loading="loading"
        @submit="handleFormSubmit"
        @cancel="dialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Edit, Delete, Link } from '@element-plus/icons-vue';
import { usePositionStore } from '@/store/positionStore';
import type { Position } from '@/types/position';
import { WORK_TYPE_OPTIONS, PRIORITY_OPTIONS } from '@/types/position';
import ApplicationStatusTag from '@/components/position/ApplicationStatusTag.vue';
import InterviewStatusTag from '@/components/position/InterviewStatusTag.vue';
import PositionForm from '@/components/position/PositionForm.vue';
import InterviewList from '@/components/interview/InterviewList.vue';
import ResourceList from '@/components/resource/ResourceList.vue';

const router = useRouter();
const route = useRoute();
const positionStore = usePositionStore();

const position = ref<Position>({} as Position);
const loading = ref(true);
const dialogVisible = ref(false);

const positionId = route.params.id as string;

onMounted(async () => {
  if (positionId) {
    await loadPosition();
    
    // 如果URL中有#interviews锚点，滚动到面试列表
    if (window.location.hash === '#interviews') {
      nextTick(() => {
        const element = document.getElementById('interviews');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  } else {
    loading.value = false;
  }
});

const loadPosition = async () => {
  try {
    loading.value = true;
    await positionStore.getPosition(Number(positionId));
    position.value = positionStore.currentPosition || {} as Position;
  } catch (error) {
    ElMessage.error('加载职位详情失败');
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  if (position.value?.company?.id) {
    router.push(`/resources/companies/${position.value.company.id}`);
  } else {
    router.push('/resources/positions');
  }
};

const handleEdit = () => {
  dialogVisible.value = true;
};

const confirmDelete = () => {
  ElMessageBox.confirm(
    '确定要删除这个职位记录吗？此操作无法恢复。',
    '确认删除',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }
  )
    .then(() => {
      handleDeletePosition();
    })
    .catch(() => {
      // 用户取消删除
    });
};

const handleDeletePosition = async () => {
  try {
    await positionStore.deletePosition(Number(positionId));
    ElMessage.success('职位删除成功');
    router.push('/resources/positions');
  } catch (error: any) {
    ElMessage.error(`删除职位失败: ${error.message || '未知错误'}`);
    console.error(error);
  }
};

const handleFormSubmit = async (updatedPosition: Partial<Position>) => {
  try {
    if (updatedPosition.id) {
      await positionStore.updatePosition(updatedPosition);
      ElMessage.success('职位更新成功');
      await loadPosition();
      dialogVisible.value = false;
    }
  } catch (error) {
    ElMessage.error('更新职位失败');
    console.error(error);
  }
};

// 当面试列表发生变化时刷新职位信息
const handleInterviewsRefresh = async () => {
  await loadPosition();
};

const handleResourceRefresh = async () => {
  await loadPosition();
};

const getWorkTypeLabel = (type: string) => {
  const option = WORK_TYPE_OPTIONS.find(opt => opt.value === type);
  return option ? option.label : type;
};

const getPriorityLabel = (priority: number) => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option ? option.label : priority;
};

const getPriorityType = (priority: number) => {
  if (priority >= 4) return 'danger';
  if (priority >= 3) return 'warning';
  if (priority >= 2) return 'success';
  return 'info';
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
};
</script>

<style scoped>
.position-detail-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.loading-container, .not-found-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 40px;
}

.position-card {
  margin-bottom: 24px;
}

.interviews-card {
  margin-top: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.position-info {
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.info-item {
  flex: 1;
  min-width: 200px;
  margin-bottom: 8px;
}

.label {
  color: #909399;
  margin-right: 8px;
  font-size: 14px;
}

.value {
  font-weight: 500;
  color: #303133;
}

.job-url-section,
.description-section,
.requirements-section {
  margin-top: 24px;
}

.content-box {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 16px;
  white-space: pre-line;
}

@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
  }
  
  .info-item {
    width: 100%;
  }
}
</style> 